import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { Button, Section, Container } from 'chaoskit/src/components'
import { linkUnderline } from 'chaoskit/src/assets/styles/utility/link'
import { text } from 'chaoskit/src/assets/styles/utility'
import useInterval from 'react-use/lib/useInterval'

import companyQuotes from './customer-quotes.json'

import ButtonLink from '~components/ButtonLink'
import Data from '~components/Data'
import { QuoteLogo } from '~components/Quote'
import Image from '~components/Image'
// import Icon from '~components/Icon'
import { ReactComponent as SelectRightSVG } from '~media/global/select-right.svg'
import { ReactComponent as SlideSVG } from '~media/global/slide.svg'

const StylesQuoteGrid = {
  content: "''",
  width: 250,
  height: 450,
  position: 'absolute',
  right: 0,
  transform: 'translateX(50%)',
  zIndex: 0,
}

const QuoteSlide = ({
  children,
  author,
  title,
  company,
  logo,
  hideCta = true,
  ...props
}) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          position: 'relative',

          '* + &': {
            marginTop: theme.space.large,
          },

          '&:not(:last-child)': {
            marginBottom: theme.space.large,
          },
        },
      ]}
      className={`G__Quote ${theme.settings.classes.trim}`}
      {...props}
    >
      {logo && <QuoteLogo company={company} logo={logo} />}
      <blockquote
        className={theme.settings.classes.trim}
        css={[
          theme.text.xlarge__fluid,
          {
            padding: 0,
            border: 0,
            position: 'relative',
            zIndex: 1,
            fontFamily: theme.fontFamily.heading,
            lineHeight: theme.lineHeight.medium,
            fontWeight: theme.fontWeight.bold,
            marginTop: theme.space.medium,
            marginBottom: theme.space.medium,

            cite: [
              theme.text.xlarge__fluid,
              {
                display: 'block',
                fontStyle: 'normal',
                marginTop: theme.fontSize.large,
                marginBottom: theme.space.small,
                color: theme.brand.teal,

                a: [
                  linkUnderline,
                  {
                    color: 'currentColor',
                  },
                ],

                span: {
                  display: 'block',
                  ...theme.text.small,
                  color: theme.fontColor.muted,
                  ...text.expanded(theme),

                  a: {
                    color: 'currentColor',
                  },
                },
              },
            ],
          },
        ]}
      >
        <div css={{ position: 'relative' }}>{children}</div>
      </blockquote>
      <div>
        {author && <div className="G__Quote__Author">{author}</div>}
        {title && (
          <div
            className="G__Quote__Title"
            css={{
              ...theme.text.base,
              color: theme.brand.gray,
              marginBottom: theme.space.medium,
            }}
          >
            {title}
          </div>
        )}
        {!hideCta && (
          <ButtonLink
            noContrast
            url="/customers/"
            type="primary"
            css={{
              [theme.mq.small__max]: {
                width: '100%',
              },
            }}
          >
            Read Case Studies
          </ButtonLink>
        )}
      </div>
    </div>
  )
}

QuoteSlide.propTypes = {
  children: PropTypes.node.isRequired,
  author: PropTypes.any,
  title: PropTypes.string,
  logo: PropTypes.string,
  company: PropTypes.string,
  hideCta: PropTypes.bool,
}

const CustomerQuotePanel = ({ customer, hideCta, activeIndex, index }) => {
  const theme = useTheme()

  return (
    <div
      aria-hidden={activeIndex !== index}
      css={{
        maxWidth: StylesContainerVariables.extended,
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container
        size="large"
        css={{
          background: 'white',
          padding: '0',
        }}
      >
        <div
          css={{
            width: '100%',
            overflow: 'hidden',

            '&::after': {
              ...StylesQuoteGrid,
              bottom: theme.space.large,
            },

            [theme.mq.medium]: {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              alignContent: 'center',
              direction: 'rtl',

              '&::after': {
                display: 'none',
              },
            },
          }}
        >
          <div
            css={{
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              position: 'relative',
              zIndex: 1,

              [theme.mq.small__max]: {
                paddingBottom: 'calc(100% / (16 / 9))',
              },
            }}
          >
            <Image
              src={customer.image}
              css={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              alt={customer.name}
            />
          </div>
          <div
            css={{
              padding: theme.space.medium,
              direction: 'ltr',
              textAlign: 'left',
              gridColumnEnd: 'span 2',
              minHeight: '410px',

              [theme.mq.medium]: {
                padding: '80px',
                minHeight: '455px',
              },
            }}
          >
            <QuoteSlide
              author={customer.name}
              title={customer.position}
              company={customer.company}
              logo={customer.logo}
              hideCta={hideCta}
            >
              <Data>{customer.quote}</Data>
            </QuoteSlide>
          </div>
        </div>
      </Container>
    </div>
  )
}

CustomerQuotePanel.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  customer: PropTypes.object.isRequired,
  hideCta: PropTypes.any,
  index: PropTypes.number.isRequired,
}

const CustomerQuote = ({ hideCta }) => {
  const theme = useTheme()

  const [manualInteraction, setManualInteraction] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [delay, setDelay] = useState(4000)

  // const previousSlide =
  //   activeIndex - 1 < 0 ? companyQuotes.quotes.length - 1 : activeIndex - 1
  const nextSlide =
    activeIndex + 1 > companyQuotes.quotes.length - 1 ? 0 : activeIndex + 1

  useInterval(() => {
    setActiveIndex(nextSlide)
  }, delay)

  useEffect(() => {
    // Stop automatically advancing slides when buttons are manually interacted with
    if (manualInteraction) {
      setDelay(null)
    }
  }, [manualInteraction])

  return (
    <Section
      space="large"
      css={{
        position: 'relative',
        background: theme.brand.violet,
        paddingBottom: `${theme.space['2xlarge']}px !important`,

        [theme.mq.small__max]: {
          padding: 0,
        },
        [theme.mq.medium]: {
          paddingTop: `120px !important`,
          paddingBottom: `140px !important`,
        },
      }}
    >
      <section
        css={{
          position: 'relative',
          overflow: 'hidden',

          '&::after': {
            ...StylesQuoteGrid,
            top: theme.space.large,
            display: 'none',

            [theme.mq.medium]: {
              display: 'block',
            },
          },
        }}
      >
        <div
          css={{
            display: 'grid',
            gridAutoColumns: '100%',
            alignItems: 'start',
            gridAutoFlow: 'column',
            transition: `transform ${theme.timing.long} ${theme.transition.base}`,
          }}
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          tabIndex="-1"
        >
          {companyQuotes.quotes.map((quote, index) => (
            <CustomerQuotePanel
              key={quote.company}
              customer={quote}
              hideCta={hideCta}
              activeIndex={activeIndex}
              index={index}
            />
          ))}
        </div>
        <div
          css={{
            position: 'absolute',
            bottom: '30px',
            zIndex: 1,
            display: 'grid',
            gridAutoColumns: 'auto',
            gridAutoFlow: 'column',
            justifyContent: 'center',
            gap: theme.space.base,
            width: '100%',
          }}
        >
          {/* <Button
            onClick={() => {
              setManualInteraction(true)
              setActiveIndex(previousSlide)
            }}
            title="Previous"
            iconOnly
            size="small"
          >
            <Icon icon="caret-left" />
          </Button> */}
          <Button
            onClick={() => {
              setManualInteraction(true)
              setActiveIndex(nextSlide)
            }}
            title="Next"
            iconOnly
            size="base"
            css={{
              height: 'auto',
              width: 'auto',
              marginLeft: '1080px',
              svg: {
                height: '50px',
                width: '50px',
              },

              [theme.mq.small__max]: {
                display: 'none',
              },
            }}
          >
            <SelectRightSVG />
          </Button>
        </div>
      </section>
      <div
        css={{
          position: 'absolute',
          bottom: '30px',
          zIndex: 1,
          display: 'grid',
          gridAutoColumns: 'auto',
          gridAutoFlow: 'column',
          justifyContent: 'center',
          gap: theme.space.base,
          width: '100%',

          [theme.mq.medium]: {
            bottom: '50px',
          },
        }}
      >
        {companyQuotes.quotes.map((quote, index) => (
          <Button
            key={quote.company}
            onClick={() => {
              setManualInteraction(true)
              setActiveIndex(index)
            }}
            iconOnly
            size="small"
            css={{
              height: 'auto',
              width: 'auto',
              svg: {
                height: '10px',
                width: '10px',
              },
            }}
          >
            <SlideSVG
              css={{ color: activeIndex === index ? '#fff' : '#9D6CFF' }}
            />
          </Button>
        ))}
      </div>
    </Section>
  )
}

CustomerQuote.propTypes = {
  hideCta: PropTypes.bool,
}

export default CustomerQuote
